import { useEffect, useState } from "react";
import { Megaphone, Users, CalendarDays } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/chalkApi";
import {
  BottomSheet, ChalkActionButton, ChalkCard, ChalkIllustrationLines,
  DarkHeader, EmptyState, SectionTitle, SportBadge, UpgradeGateCard,
} from "../components/chalk/primitives";

export default function League() {
  const { user } = useAuth();
  const [data, setData] = useState({ teams: [], total_teams: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [castOpen, setCastOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const d = await api.leagueOverview();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (user.role !== "league_admin") {
    return (
      <>
        <DarkHeader eyebrow="League OS" title="League Admin" subtitle="League admin only." />
        <div style={{ padding: "0 18px 24px" }}>
          <UpgradeGateCard
            featureName="League OS"
            benefit="Run every team's calendar, broadcast leaguewide, and manage coaches from one board."
            onUpgrade={() => { /* contact sales */ }}
          />
        </div>
      </>
    );
  }

  const totalAthletes = data.teams.reduce((s, t) => s + (t.athletes || 0), 0);
  const totalUpcoming = data.teams.reduce((s, t) => s + (t.upcoming_events || 0), 0);

  return (
    <>
      <DarkHeader
        eyebrow="League OS"
        title="League Admin"
        subtitle={loading ? "Loading…" : `${data.total_teams} team${data.total_teams === 1 ? "" : "s"} · ${totalAthletes} athletes`}
      />
      <div style={{ padding: "0 18px 24px" }} data-testid="league-page">
        {error ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="Couldn't load the league."
            body={error.message}
          />
        ) : (
          <>
            <SectionTitle label="At a glance" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
              <StatTile icon={<Users size={16} />} value={data.total_teams} label="Teams" />
              <StatTile icon={<Users size={16} />} value={totalAthletes} label="Athletes" />
              <StatTile icon={<CalendarDays size={16} />} value={totalUpcoming} label="Upcoming" />
            </div>

            <SectionTitle label="Quick Draw" />
            <ChalkActionButton
              testId="league-broadcast-btn"
              variant="primary"
              label="Send league-wide Chalk Talk"
              icon={<Megaphone size={16} aria-hidden="true" />}
              onClick={() => setCastOpen(true)}
            />

            <SectionTitle label="Teams" />
            {data.teams.length === 0 ? (
              <EmptyState
                icon={<ChalkIllustrationLines />}
                headline="No league teams yet."
                body="Invite a coach to get rolling."
              />
            ) : (
              data.teams.map((t) => (
                <ChalkCard key={t.id} data-testid={`league-team-${t.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>{t.name}</div>
                      <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
                        {t.athletes} athlete{t.athletes === 1 ? "" : "s"} · {t.upcoming_events} upcoming · {t.status}
                      </div>
                    </div>
                    <SportBadge sport={t.sport} />
                  </div>
                </ChalkCard>
              ))
            )}
          </>
        )}
      </div>

      <BroadcastSheet
        isOpen={castOpen}
        onDismiss={() => setCastOpen(false)}
        onSent={(n) => { setCastOpen(false); toast.success(`Pinned to ${n} team${n === 1 ? "" : "s"}.`); }}
      />
    </>
  );
}

function StatTile({ icon, value, label }) {
  return (
    <div style={{
      background: "#fff", border: "0.5px solid var(--chalk-border)",
      borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 6,
    }}>
      <span style={{ color: "var(--chalk-green)" }}>{icon}</span>
      <div style={{ fontSize: 22, color: "var(--chalk-board)", fontWeight: 500, fontFamily: "var(--font-serif, 'DM Serif Display', serif)" }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </div>
    </div>
  );
}

function BroadcastSheet({ isOpen, onDismiss, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (isOpen) setBody(""); }, [isOpen]);

  async function send() {
    if (!body.trim()) return;
    setSending(true);
    try {
      const d = await api.leagueBroadcast(body.trim());
      onSent && onSent(d.sent_to || 0);
    } catch (e) {
      toast.error(e.message || "Couldn't broadcast.");
    } finally {
      setSending(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Broadcast to every team" onDismiss={onDismiss}>
      <div style={{ display: "grid", gap: 12 }} data-testid="broadcast-sheet">
        <div style={{ fontSize: 13, color: "var(--chalk-muted)" }}>
          Pinned to every active team's Chalk Talk. Goes out as a push notification too.
        </div>
        <textarea
          data-testid="broadcast-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="Field maintenance Tuesday — please check the schedule."
          style={{
            width: "100%", border: "0.5px solid var(--chalk-border)",
            borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none",
            background: "#FBF8F2", color: "var(--chalk-board)",
            resize: "vertical", fontFamily: "inherit",
          }}
        />
        <ChalkActionButton
          testId="broadcast-send"
          variant="primary"
          label={sending ? "Broadcasting…" : "Send to every team"}
          icon={<Megaphone size={14} aria-hidden="true" />}
          loading={sending}
          disabled={!body.trim()}
          onClick={send}
        />
      </div>
    </BottomSheet>
  );
}
